import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { IssueStatusIcon } from "@productlane/ui"
import { CommandItem } from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { SharedFilterItemsProps } from "./types"
import { useThreadsFilterState } from "../threads-filter-state"

export interface IssueStatusOption {
	name: string
	status: string
	color: string
}

export function distinctIssueStatuses(
	issues: ReadonlyArray<{
		statusName?: string | null
		status: string
		statusColor?: string | null
	}>,
): IssueStatusOption[] {
	const seen = new Map<string, IssueStatusOption>()
	for (const issue of issues) {
		if (!issue.statusName || seen.has(issue.statusName)) continue
		seen.set(issue.statusName, {
			name: issue.statusName,
			status: issue.status,
			color: issue.statusColor ?? "",
		})
	}
	return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
}

export function useIssueStatusOptions() {
	// Same args as the Issue filter, so both share one subscription.
	const [issues] = useQuery(
		queries.issues({ orderBy: "updatedAt", order: "desc" }),
	)
	return useMemo(() => distinctIssueStatuses(issues), [issues])
}

export function IssueStatusFilterItems({ onSelect }: SharedFilterItemsProps) {
	const options = useIssueStatusOptions()
	const { issueStatuses, addIssueStatus, removeIssueStatus } =
		useThreadsFilterState()

	return (
		<>
			{options.map((option) => {
				const isSelected = issueStatuses.includes(option.name)
				return (
					<CommandItem
						key={option.name}
						value={option.name}
						onSelect={() => {
							if (isSelected) {
								removeIssueStatus(option.name)
							} else {
								addIssueStatus(option.name)
							}
							onSelect()
						}}
					>
						<span className="flex w-full items-center gap-2">
							<IssueStatusIcon
								color={option.color}
								status={option.status}
								className="h-3.5 w-3.5 flex-none"
							/>
							<span className="flex-1 truncate">{option.name}</span>
							<Check
								className={cn("h-4 w-4 flex-none", {
									"opacity-100": isSelected,
									"opacity-0": !isSelected,
								})}
							/>
						</span>
					</CommandItem>
				)
			})}
		</>
	)
}
